import { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Stack,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Select,
  MenuItem,
  TextField,
  FormControl,
  InputLabel,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VariableIcon from '@mui/icons-material/DataObject';
import { abyssColors } from '@/theme/colors';
import {
  AssemblyTemplateField,
  TemplateTypeEnum,
  LogicEvaluationStrategy,
} from '@/__generated__/graphql';

interface ConditionNode {
  id: string;
  nodeType: 'SIMPLE' | 'GROUP';
  inputVariable?: string;
  operator?: string;
  value?: string;
  logicOperator?: 'AND' | 'OR';
  conditions?: ConditionNode[];
}

interface Variable {
  name: string;
  fieldKey: string;
  templateType: string;
  isBlisterField?: boolean;
}

interface ConditionalMapping {
  id: string;
  value: string;
  conditionTree: ConditionNode;
  combineOperator?: string;
}

interface ConditionBuilderProps {
  availableFields: AssemblyTemplateField[];
  conditionalMappings: ConditionalMapping[];
  onUpdate: (mappings: ConditionalMapping[]) => void;
  variables?: Variable[];
  onVariablesUpdate?: (variables: Variable[]) => void;
  evaluationStrategy?: LogicEvaluationStrategy;
  onEvaluationStrategyUpdate?: (strategy: LogicEvaluationStrategy) => void;

  poiTemplates?: any[]; // POI templates for variable selection
}

const BLISTER_FIELDS = [
  { key: 'estimatedHeight', name: 'Estimated Height' },
  { key: 'manualSetHeight', name: 'Manual Set Height' },
  { key: 'blisterHeightIndex', name: 'Blister Height Index' },
  { key: 'maxHeight', name: 'Max Height' },
  { key: 'wallLoss', name: 'Wall Loss' },
  { key: 'residualThickness', name: 'Residual Thickness' },
  { key: 'residualCorrosionAllowance', name: 'Residual Corrosion Allowance' },
  { key: 'pdrHeight', name: 'PDR Height' },
  { key: 'verifiedMaxHeight', name: 'Verified Max Height' },
  { key: 'verifiedResidualThickness', name: 'Verified Residual Thickness' },
  { key: 'verifiedResidualCorrosionAllowance', name: 'Verified Residual Corrosion Allowance' },
];

const OPERATORS = [
  { value: 'EQUAL', label: 'Equals (=)' },
  { value: 'NOT_EQUAL', label: 'Not Equals (≠)' },
  { value: 'GREATER_THAN', label: 'Greater Than (>)' },
  { value: 'LESS_THAN', label: 'Less Than (<)' },
  { value: 'GREATER_THAN_OR_EQUAL', label: 'Greater Than or Equal (≥)' },
  { value: 'LESS_THAN_OR_EQUAL', label: 'Less Than or Equal (≤)' },
];

export const ConditionBuilder = ({
  availableFields,
  conditionalMappings,
  onUpdate,
  variables = [],
  onVariablesUpdate,
  evaluationStrategy = LogicEvaluationStrategy.FirstMatch,
  onEvaluationStrategyUpdate,
  poiTemplates = [],
}: ConditionBuilderProps) => {
  const [mappings, setMappings] = useState<ConditionalMapping[]>(conditionalMappings);
  const [localVariables, setLocalVariables] = useState<Variable[]>(variables);
  const [newVariableName, setNewVariableName] = useState('');
  const [selectedTemplateType, setSelectedTemplateType] = useState<TemplateTypeEnum>(
    TemplateTypeEnum.Assembly
  );
  const [isBlisterField, setIsBlisterField] = useState(false);
  const [selectedField, setSelectedField] = useState<AssemblyTemplateField | null>(null);
  const [localEvaluationStrategy, setLocalEvaluationStrategy] =
    useState<LogicEvaluationStrategy>(evaluationStrategy);

  // Sync variables when prop changes
  useEffect(() => {
    setLocalVariables(variables);
  }, [variables]);

  // Sync evaluation strategy when prop changes
  useEffect(() => {
    setLocalEvaluationStrategy(evaluationStrategy);
  }, [evaluationStrategy]);

  // Get available fields based on selected template type
  const availableFieldsForVariable = useMemo(() => {
    if (selectedTemplateType === TemplateTypeEnum.PointOfInterest) {
      if (isBlisterField) {
        // Return hardcoded blister fields
        return BLISTER_FIELDS as any;
      }
      // Get all POI fields from all POI templates
      const allPoiFields: AssemblyTemplateField[] = [];
      poiTemplates?.forEach((template) => {
        if (template.fields) {
          allPoiFields.push(...template.fields);
        }
      });
      // Remove duplicates by key
      const uniqueFields = allPoiFields.reduce((acc, field) => {
        if (!acc.find((f) => f.key === field.key)) {
          acc.push(field);
        }
        return acc;
      }, [] as AssemblyTemplateField[]);
      return uniqueFields;
    }
    return availableFields;
  }, [selectedTemplateType, isBlisterField, poiTemplates, availableFields]);

  const handleAddVariable = () => {
    if (!newVariableName.trim() || !selectedField) return;

    const newVariable: Variable = {
      name: newVariableName.trim(),
      fieldKey: selectedField.key || '',
      templateType: selectedTemplateType,
      isBlisterField: isBlisterField,
    };

    const updatedVariables = [...localVariables, newVariable];
    setLocalVariables(updatedVariables);
    if (onVariablesUpdate) {
      onVariablesUpdate(updatedVariables);
    }

    // Reset form
    setNewVariableName('');
    setSelectedField(null);
    setSelectedTemplateType(TemplateTypeEnum.Assembly);
    setIsBlisterField(false);
  };

  const handleDeleteVariable = (variableName: string) => {
    const updatedVariables = localVariables.filter((v) => v.name !== variableName);
    setLocalVariables(updatedVariables);
    if (onVariablesUpdate) {
      onVariablesUpdate(updatedVariables);
    }
  };

  const createNewCondition = (): ConditionNode => ({
    id: `condition-${Date.now()}-${Math.random()}`,
    nodeType: 'SIMPLE',
    inputVariable: '',
    operator: 'EQUAL',
    value: '',
    logicOperator: 'AND',
  });

  const createNewMapping = (): ConditionalMapping => ({
    id: `mapping-${Date.now()}`,
    value: '',
    conditionTree: createNewCondition(),
  });

  const handleAddMapping = () => {
    const newMappings = [...mappings, createNewMapping()];
    setMappings(newMappings);
    onUpdate(newMappings);
  };

  const handleDeleteMapping = (mappingId: string) => {
    const newMappings = mappings.filter((m) => m.id !== mappingId);
    setMappings(newMappings);
    onUpdate(newMappings);
  };

  const handleUpdateMapping = (mappingId: string, field: keyof ConditionalMapping, value: any) => {
    const newMappings = mappings.map((m) => (m.id === mappingId ? { ...m, [field]: value } : m));
    setMappings(newMappings);
    onUpdate(newMappings);
  };

  const handleUpdateCondition = (
    mappingId: string,
    conditionId: string,
    field: keyof ConditionNode,

    value: any
  ) => {
    const newMappings = mappings.map((mapping) => {
      if (mapping.id === mappingId) {
        const updateConditionRecursive = (condition: ConditionNode): ConditionNode => {
          if (condition.id === conditionId) {
            return { ...condition, [field]: value };
          }
          if (condition.conditions) {
            return {
              ...condition,
              conditions: condition.conditions.map(updateConditionRecursive),
            };
          }
          return condition;
        };

        return {
          ...mapping,
          conditionTree: updateConditionRecursive(mapping.conditionTree),
        };
      }
      return mapping;
    });
    setMappings(newMappings);
    onUpdate(newMappings);
  };

  const handleAddConditionToGroup = (mappingId: string, parentConditionId: string) => {
    const newMappings = mappings.map((mapping) => {
      if (mapping.id === mappingId) {
        const addConditionRecursive = (condition: ConditionNode): ConditionNode => {
          if (condition.id === parentConditionId) {
            return {
              ...condition,
              nodeType: 'GROUP',
              conditions: [...(condition.conditions || []), createNewCondition()],
            };
          }
          if (condition.conditions) {
            return {
              ...condition,
              conditions: condition.conditions.map(addConditionRecursive),
            };
          }
          return condition;
        };

        return {
          ...mapping,
          conditionTree: addConditionRecursive(mapping.conditionTree),
        };
      }
      return mapping;
    });
    setMappings(newMappings);
    onUpdate(newMappings);
  };

  const handleDeleteCondition = (mappingId: string, conditionId: string) => {
    const newMappings = mappings.map((mapping) => {
      if (mapping.id === mappingId) {
        const deleteConditionRecursive = (condition: ConditionNode): ConditionNode | null => {
          // If this is the condition to delete, return null
          if (condition.id === conditionId) {
            return null;
          }

          // If this condition has nested conditions, filter them
          if (condition.conditions) {
            const filteredConditions = condition.conditions
              .map(deleteConditionRecursive)
              .filter((c): c is ConditionNode => c !== null);

            // If only one condition left in group, convert back to single condition
            if (filteredConditions.length === 1) {
              return filteredConditions[0];
            }

            // If no conditions left, this shouldn't happen but handle it
            if (filteredConditions.length === 0) {
              return null;
            }

            return {
              ...condition,
              conditions: filteredConditions,
            };
          }

          return condition;
        };

        const updatedTree = deleteConditionRecursive(mapping.conditionTree);

        // If the root condition was deleted, create a new one
        return {
          ...mapping,
          conditionTree: updatedTree || createNewCondition(),
        };
      }
      return mapping;
    });
    setMappings(newMappings);
    onUpdate(newMappings);
  };

  const renderCondition = (
    condition: ConditionNode,
    mappingId: string,
    depth: number = 0
  ): JSX.Element => {
    const isGroup = condition.nodeType === 'GROUP' && condition.conditions;

    return (
      <Card
        key={condition.id}
        elevation={0}
        sx={{
          ml: depth * 3,
          mb: 1,
          backgroundColor: isGroup ? abyssColors.tertiary[50] : abyssColors.neutral[50],
          border: `1px solid ${isGroup ? abyssColors.tertiary[200] : abyssColors.neutral[200]}`,
        }}
      >
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Stack spacing={2}>
            {/* Condition Type */}
            {depth === 0 && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  label={condition.nodeType}
                  size="small"
                  sx={{
                    backgroundColor: abyssColors.primary[100],
                    color: abyssColors.primary[700],
                    fontSize: '0.875rem',
                  }}
                />
                {isGroup && (
                  <Chip
                    label={`${condition.conditions?.length || 0} conditions`}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.875rem' }}
                  />
                )}
              </Stack>
            )}

            {!isGroup ? (
              <>
                {/* Single Condition */}
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" gap={1}>
                  {/* Delete Button (only show for nested conditions) */}
                  {depth > 0 && (
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteCondition(mappingId, condition.id)}
                      sx={{ color: abyssColors.error[500] }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}

                  {/* Field/Variable Selection */}
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Field / Variable</InputLabel>
                    <Select
                      value={condition.inputVariable || ''}
                      onChange={(e) =>
                        handleUpdateCondition(
                          mappingId,
                          condition.id,
                          'inputVariable',
                          e.target.value
                        )
                      }
                      label="Field / Variable"
                    >
                      {localVariables.length > 0 && [
                        <MenuItem
                          key="variables-header"
                          disabled
                          sx={{ fontWeight: 600, fontSize: '0.875rem' }}
                        >
                          Variables
                        </MenuItem>,
                        ...localVariables.map((variable) => (
                          <MenuItem key={`var-${variable.name}`} value={variable.name}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <VariableIcon
                                sx={{ fontSize: '1rem', color: abyssColors.tertiary[500] }}
                              />
                              <Typography variant="body2">{variable.name}</Typography>
                              <Chip
                                label={variable.fieldKey}
                                size="small"
                                sx={{ height: '18px', fontSize: '0.75rem' }}
                              />
                            </Stack>
                          </MenuItem>
                        )),
                        <Divider key="divider" />,
                        <MenuItem
                          key="fields-header"
                          disabled
                          sx={{ fontWeight: 600, fontSize: '0.875rem' }}
                        >
                          Direct Fields
                        </MenuItem>,
                      ]}
                      {availableFields.map((field) => (
                        <MenuItem key={field.id} value={field.key || ''}>
                          {field.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Operator Selection */}
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Operator</InputLabel>
                    <Select
                      value={condition.operator || 'EQUAL'}
                      onChange={(e) =>
                        handleUpdateCondition(mappingId, condition.id, 'operator', e.target.value)
                      }
                      label="Operator"
                    >
                      {OPERATORS.map((op) => (
                        <MenuItem key={op.value} value={op.value}>
                          {op.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Value Input */}
                  <TextField
                    size="small"
                    label="Value"
                    value={condition.value || ''}
                    onChange={(e) =>
                      handleUpdateCondition(mappingId, condition.id, 'value', e.target.value)
                    }
                    sx={{ minWidth: 150 }}
                  />

                  {/* Convert to Group Button */}
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => handleAddConditionToGroup(mappingId, condition.id)}
                  >
                    Add AND/OR Group
                  </Button>
                </Stack>
              </>
            ) : (
              <>
                {/* Group of Conditions */}
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" fontWeight={600}>
                    Logic Operator:
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 100 }}>
                    <Select
                      value={condition.logicOperator || 'AND'}
                      onChange={(e) =>
                        handleUpdateCondition(
                          mappingId,
                          condition.id,
                          'logicOperator',
                          e.target.value
                        )
                      }
                    >
                      <MenuItem value="AND">AND</MenuItem>
                      <MenuItem value="OR">OR</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>

                {/* Nested Conditions */}
                {condition.conditions?.map((nestedCondition) =>
                  renderCondition(nestedCondition, mappingId, depth + 1)
                )}

                {/* Add Another Condition to Group */}
                <Button
                  size="small"
                  variant="text"
                  startIcon={<AddIcon />}
                  onClick={() => handleAddConditionToGroup(mappingId, condition.id)}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Add Another Condition
                </Button>
              </>
            )}
          </Stack>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box>
      <Stack spacing={3}>
        {/* Variable Management Section */}
        <Accordion
          defaultExpanded
          elevation={0}
          sx={{ border: `1px solid ${abyssColors.neutral[200]}` }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <VariableIcon sx={{ color: abyssColors.tertiary[500] }} />
              <Typography variant="subtitle2" fontWeight={600}>
                Variables
              </Typography>
              <Chip
                label={`${localVariables.length} defined`}
                size="small"
                sx={{
                  backgroundColor: abyssColors.tertiary[50],
                  color: abyssColors.tertiary[700],
                  height: '20px',
                }}
              />
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Typography variant="caption" color="text.secondary">
                Define variables to reference field values in your conditions. Variables make your
                logic more readable and maintainable.
              </Typography>

              {/* Add Variable Form */}
              <Card elevation={0} sx={{ backgroundColor: abyssColors.neutral[50], border: 'none' }}>
                <CardContent>
                  <Stack spacing={2}>
                    <Typography variant="body2" fontWeight={600}>
                      Add New Variable
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={2}
                      alignItems="flex-start"
                      flexWrap="wrap"
                      gap={1}
                    >
                      <TextField
                        size="small"
                        label="Variable Name"
                        value={newVariableName}
                        onChange={(e) => setNewVariableName(e.target.value)}
                        placeholder="e.g., thickness, pressure"
                        sx={{ minWidth: 200 }}
                      />
                      <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel>Template Type</InputLabel>
                        <Select
                          value={selectedTemplateType}
                          onChange={(e) => {
                            setSelectedTemplateType(e.target.value as TemplateTypeEnum);
                            setSelectedField(null); // Reset field when template type changes
                            setIsBlisterField(false); // Reset blister field when template type changes
                          }}
                          label="Template Type"
                        >
                          <MenuItem value={TemplateTypeEnum.Assembly}>Assembly</MenuItem>
                          <MenuItem value={TemplateTypeEnum.PointOfInterest}>
                            Point of Interest
                          </MenuItem>
                        </Select>
                      </FormControl>
                      {selectedTemplateType === TemplateTypeEnum.PointOfInterest && (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={isBlisterField}
                              onChange={(e) => {
                                setIsBlisterField(e.target.checked);
                                setSelectedField(null); // Reset field when checkbox changes
                              }}
                              size="small"
                            />
                          }
                          label="Blister Field"
                          sx={{ minWidth: 140 }}
                        />
                      )}
                      <Autocomplete
                        size="small"
                        options={availableFieldsForVariable}
                        getOptionLabel={(option) => option.name || ''}
                        value={selectedField}
                        onChange={(_, newValue) => setSelectedField(newValue)}
                        renderInput={(params) => <TextField {...params} label="Link to Field" />}
                        renderOption={(props, option) => (
                          <li {...props}>
                            <Stack>
                              <Typography variant="body2">{option.name}</Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontFamily: 'monospace' }}
                              >
                                {option.key}
                              </Typography>
                            </Stack>
                          </li>
                        )}
                        sx={{ minWidth: 250 }}
                      />
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAddVariable}
                        disabled={!newVariableName.trim() || !selectedField}
                        sx={{
                          backgroundColor: abyssColors.tertiary[500],
                          '&:hover': { backgroundColor: abyssColors.tertiary[600] },
                        }}
                      >
                        Add Variable
                      </Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>

              {/* Variables List */}
              {localVariables.length > 0 && (
                <Box>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                    Defined Variables
                  </Typography>
                  <Stack spacing={1}>
                    {localVariables.map((variable) => (
                      <Card
                        key={variable.name}
                        elevation={0}
                        sx={{
                          backgroundColor: 'white',
                          border: `1px solid ${abyssColors.neutral[200]}`,
                        }}
                      >
                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Stack direction="row" spacing={2} alignItems="center">
                              <VariableIcon sx={{ color: abyssColors.tertiary[500] }} />
                              <Typography
                                variant="body2"
                                fontWeight={500}
                                sx={{ fontFamily: 'monospace' }}
                              >
                                {variable.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                →
                              </Typography>
                              <Chip
                                label={variable.fieldKey}
                                size="small"
                                sx={{ fontSize: '1rem', color: abyssColors.neutral[50] }}
                              />
                              {variable.templateType && (
                                <Chip
                                  label={variable.templateType}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontSize: '1rem' }}
                                />
                              )}
                            </Stack>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteVariable(variable.name)}
                              sx={{ color: abyssColors.error[500] }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                </Box>
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* Evaluation Strategy Section */}
        <Divider />
        <Stack spacing={2}>
          <Typography variant="subtitle2" fontWeight={600}>
            Evaluation Strategy
          </Typography>
          <FormControl size="small" sx={{ maxWidth: 300 }}>
            <InputLabel>Strategy</InputLabel>
            <Select
              value={localEvaluationStrategy}
              onChange={(e) => {
                const newStrategy = e.target.value as LogicEvaluationStrategy;
                setLocalEvaluationStrategy(newStrategy);
                if (onEvaluationStrategyUpdate) {
                  onEvaluationStrategyUpdate(newStrategy);
                }
              }}
              label="Strategy"
            >
              <MenuItem value={LogicEvaluationStrategy.FirstMatch}>
                First Match - Stop at first matching condition
              </MenuItem>
              <MenuItem value={LogicEvaluationStrategy.AllMatch}>
                All Match - All conditions must match
              </MenuItem>
              <MenuItem value={LogicEvaluationStrategy.AnyMatch}>
                Any Match - At least one condition must match
              </MenuItem>
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary">
            {localEvaluationStrategy === LogicEvaluationStrategy.FirstMatch &&
              'Evaluates conditions in order and returns the value of the first matching condition.'}
            {localEvaluationStrategy === LogicEvaluationStrategy.AllMatch &&
              'All conditional mappings must match for the rule to be satisfied.'}
            {localEvaluationStrategy === LogicEvaluationStrategy.AnyMatch &&
              'At least one conditional mapping must match for the rule to be satisfied.'}
          </Typography>
        </Stack>

        {/* Conditional Mappings Section */}
        <Divider />
        <Typography variant="subtitle2" fontWeight={600}>
          Conditional Mappings
        </Typography>

        {mappings.length === 0 ? (
          <Card elevation={0} sx={{ backgroundColor: abyssColors.neutral[50] }}>
            <CardContent>
              <Stack alignItems="center" spacing={2} py={3}>
                <Typography variant="body2" color="text.secondary">
                  No conditional mappings configured
                </Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddMapping}>
                  Add First Mapping
                </Button>
              </Stack>
            </CardContent>
          </Card>
        ) : (
          mappings.map((mapping, index) => (
            <Card key={mapping.id} elevation={0} variant="outlined">
              <CardContent>
                <Stack spacing={2}>
                  {/* Mapping Header */}
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="subtitle2" fontWeight={600}>
                        Mapping {index + 1}
                      </Typography>
                      <Chip
                        label="IF-THEN"
                        size="small"
                        color="primary"
                        sx={{ fontSize: '0.875rem' }}
                      />
                    </Stack>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteMapping(mapping.id)}
                      sx={{ color: abyssColors.error[500] }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Stack>

                  <Divider />

                  {/* Output Value */}
                  <Stack spacing={1}>
                    <Typography variant="body2" fontWeight={600}>
                      THEN Set Value To:
                    </Typography>
                    <TextField
                      size="small"
                      label="Output Value"
                      value={mapping.value}
                      onChange={(e) => handleUpdateMapping(mapping.id, 'value', e.target.value)}
                      placeholder="Enter the value to set when conditions match"
                      fullWidth
                    />
                  </Stack>

                  <Divider />

                  {/* Conditions */}
                  <Stack spacing={1}>
                    <Typography variant="body2" fontWeight={600}>
                      IF Conditions:
                    </Typography>
                    {renderCondition(mapping.conditionTree, mapping.id)}
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          ))
        )}

        {/* Add Mapping Button */}
        {mappings.length > 0 && (
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddMapping}
            sx={{ alignSelf: 'flex-start' }}
          >
            Add Another Mapping
          </Button>
        )}
      </Stack>
    </Box>
  );
};
